import { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus, Upload, Trash2, X, CheckCircle2, XCircle, RefreshCw, Shield } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { useToast } from '../../../hooks/use-toast';
import { myLocalStorage } from '../../StorageHelper';
import { WhitelistDetails, WhitelistHash } from './interface';
import { deleteHashes, submitHashes, toggleEnableForAgentUpdate, toggleEnableForWhiteList, whiteListingDetails } from './Api/policyprofileapi';
import { formatDate } from '../formatTimeAgo';
import { POLICY_PROFILES_PATH } from './helper';

interface HashEntry {
    id: string;
    value: string;
    type: string;
    createdAt: string;
    label?: string;
}

interface SettingsData {
    agentAutoUpdate: boolean;
    sha256WhitelistingEnabled: boolean;
    whitelistHashes: HashEntry[];
}

export default function PolicyProfilesSettings() {
    const location = useLocation();
    const navigate = useNavigate();
    const { osType: osTypeParam } = useParams();
    const profileName = location.state?.profileName || localStorage.getItem('currentProfileName') || '';
    const [profile, setProfile] = useState(() => myLocalStorage.getItem("profilesDetails"));

    const osType = osTypeParam || 'windows';
    const { toast } = useToast();
    const [activeSection, setActiveSection] = useState('agent-auto-update');

    const [agentAutoUpdate, setAgentAutoUpdate] = useState<boolean>(false);
    const [sha256WhitelistingEnabled, setSha256WhitelistingEnabled] = useState<boolean>(false);
    const [whitelistHashes, setWhitelistHashes] = useState<WhitelistDetails>();

    const [showAddHashDialog, setShowAddHashDialog] = useState(false);
    const [hashDialogTab, setHashDialogTab] = useState('manual');
    const [newHashInput, setNewHashInput] = useState('');
    const [pendingHashes, setPendingHashes] = useState<string[]>([]);
    const [selectedHashes, setSelectedHashes] = useState<string[]>([]);
    const [hashSearchTerm, setHashSearchTerm] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [settingsSearchTerm, setSettingsSearchTerm] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const filteredHashes: WhitelistHash[] = (whitelistHashes?.whitelistHashes || []).filter(
        (hash) =>
            hash.hashValue.toLowerCase().includes(hashSearchTerm.toLowerCase()) ||
            hash.whitelistLabel?.toLowerCase().includes(hashSearchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredHashes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedHashes = filteredHashes.slice(startIndex, startIndex + itemsPerPage);

    const handleToggleSetting = async (
        setting: 'sha256WhitelistingEnabled' | 'agentAutoUpdate'
    ) => {
        if (setting === 'sha256WhitelistingEnabled') {
            const newValue = !sha256WhitelistingEnabled;
            setSha256WhitelistingEnabled(newValue); // immediate state change
            const updatedProfile = { ...profile, isWhitelistEnabled: newValue };
            setProfile(updatedProfile);
            myLocalStorage.setItem("profilesDetails", updatedProfile);
            try {
                await toggleEnableForWhiteList(profile.id, newValue);
            } catch (error) {
                // revert UI if API fails
                setSha256WhitelistingEnabled(!newValue);
                toast({
                    title: "Failed to update whitelist setting",
                    variant: "destructive",
                });
            }
        } else if (setting === 'agentAutoUpdate') {
            const newValue = !agentAutoUpdate;
            setAgentAutoUpdate(newValue);
            const updatedProfile = { ...profile, autoAgentUpgradeEnabled: newValue };
            setProfile(updatedProfile);
            myLocalStorage.setItem("profilesDetails", updatedProfile);
            try {
                await toggleEnableForAgentUpdate(profile.id, newValue);
            } catch (error) {
                setAgentAutoUpdate(!newValue);
                toast({
                    title: "Failed to update agent auto-update setting",
                    variant: "destructive",
                });
            }
        }
    };



    const handleAddHash = () => {
        const hashValue = newHashInput.trim();

        if (!hashValue) return;

        // Basic SHA-256 validation (64 hex characters)
        if (!/^[a-fA-F0-9]{64}$/.test(hashValue)) {
            toast({
                title: "Invalid hash format",
                description: "Please enter a valid SHA-256 hash (64 hex characters)",
                variant: "destructive",
            });
            return;
        }

        // Check for duplicates (case-insensitive)
        const isDuplicate = pendingHashes.some(
            (hash) => hash.toLowerCase() === hashValue.toLowerCase()
        );

        if (isDuplicate) {
            toast({
                title: "Duplicate hash",
                description: "This hash is already in the list.",
                variant: "destructive",
            });
            return;
        }

        setPendingHashes((prev) => [...prev, hashValue]);
        setNewHashInput("");
    };


    const handleRemovePendingHash = (hashToRemove: string) => {
        setPendingHashes(prev => prev.filter(hash => hash !== hashToRemove));
    };

    const handleSubmitHashes = async () => {
        if (pendingHashes.length === 0) return;

        const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

        const payload = {
            profileId: profile.id,
            isWhitelistEnabled: sha256WhitelistingEnabled,
            whitelistHashes: pendingHashes.map((hash) => ({
                hash,
                label: currentDate,
            })),
        };

        try {
            await submitHashes(payload);
            toast({
                title: "Hashes submitted successfully",
                description: `${pendingHashes.length} hash${pendingHashes.length > 1 ? "es" : ""} added to whitelist.`,
            });
            setPendingHashes([]);
            setShowAddHashDialog(false);
        } catch (error) {
            toast({
                title: "Submission failed",
                description: "An error occurred while submitting hashes.",
                variant: "destructive",
            });
        }
        getWhiteListingDetails()
    };


    const handleSelectHash = (hashValue: string, checked: boolean) => {
        if (checked) {
            setSelectedHashes(prev => [...prev, hashValue]);
        } else {
            setSelectedHashes(prev => prev.filter(id => id !== hashValue));
        }
    };

    const handleDeleteSelected = () => {
        if (selectedHashes.length === 0) return;
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (selectedHashes.length === 0) return;

        const uniqueHashes = Array.from(
            new Set(selectedHashes.map((hash) => hash.toLowerCase()))
        );

        const payload = {
            hashList: uniqueHashes,
        };

        try {
            await deleteHashes(payload, profile.id);

            const count = uniqueHashes.length;
            setSelectedHashes([]);
            setShowDeleteDialog(false);

            toast({
                title: `${count} hash${count > 1 ? "es" : ""} deleted successfully`,
            });
        } catch (error) {
            toast({
                title: "Deletion failed",
                description: "An error occurred while deleting hashes.",
                variant: "destructive",
            });
        }
    };


    const processFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            const hashes = content.split('\n')
                .map(line => line.trim())
                .filter(line => line && /^[a-fA-F0-9]{64}$/.test(line));

            setPendingHashes(prev => [...prev, ...hashes]);
            toast({ title: `${hashes.length} hash(es) loaded from file` });
        };
        reader.readAsText(file);
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        processFile(file);
        // Reset the input
        event.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const file = files[0];

        if (file && (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.csv'))) {
            processFile(file);
        } else {
            toast({ title: "Invalid file type", description: "Please upload a text (.txt) or CSV (.csv) file" });
        }
    };

    const sidebarItems = [
        { id: 'agent-auto-update', label: 'Agent Auto Update', icon: RefreshCw },
        { id: 'whitelisting-hashes', label: 'Whitelisting Hashes', icon: Shield },
    ];

    // Filter sidebar items based on OS type and search
    const filteredSidebarItems = sidebarItems
        .filter(item => {
            // Hide Whitelisting Hashes for Linux profiles
            if (item.id === 'whitelisting-hashes' && osType === 'linux') {
                return false;
            }
            return true;
        })
        .filter(item =>
            item.label.toLowerCase().includes(settingsSearchTerm.toLowerCase())
        );

    const getWhiteListingDetails = async () => {
        let response: WhitelistDetails = await whiteListingDetails(profile.id)
        setWhitelistHashes(response);
    }


    useEffect(() => {
        if (activeSection !== "agent-auto-update") {
            getWhiteListingDetails();
        }
    }, [activeSection])


    useEffect(() => {
        setAgentAutoUpdate(profile.autoAgentUpgradeEnabled)
        setSha256WhitelistingEnabled(profile.isWhitelistEnabled)
    }, [profile])

    const handleBackToProfiles = () => {
        navigate(POLICY_PROFILES_PATH);
    };

    return (
        <div className="h-full flex">
            {/* Sidebar */}
            <div className="w-64 bg-sidebar border-r border-border">
                <div className="p-4 border-b border-border">
                    <Button
                        variant="ghost"
                        type="button"
                        className="mb-4 justify-start p-0 h-auto text-sidebar-foreground hover:text-sidebar-accent-foreground"
                        onClick={handleBackToProfiles}
                        data-testid="button-back"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Profiles
                    </Button>
                    <div>
                        <h2 className="font-semibold text-sidebar-foreground">{profileName}</h2>
                        <p className="text-sm text-sidebar-muted-foreground capitalize">{osType} Profile Settings</p>
                    </div>
                </div>

                <div className="p-2">
                    {/* Settings Search Bar */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                            placeholder="Search"
                            value={settingsSearchTerm}
                            onChange={(e) => setSettingsSearchTerm(e.target.value)}
                            className="pl-10 h-8 bg-sidebar-accent/20 border-sidebar-border"
                            data-testid="input-search-settings"
                        />
                    </div>

                    <div className="text-xs font-medium text-sidebar-muted-foreground uppercase tracking-wide px-3 py-2">
                        Settings
                    </div>
                    {filteredSidebarItems.map((item) => (
                        <Button
                            key={item.id}
                            variant="ghost"
                            className={`w-full justify-start mb-1 ${activeSection === item.id
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground'
                                }`}
                            onClick={() => setActiveSection(item.id)}
                            data-testid={`button-${item.id}`}
                        >
                            <item.icon className="w-4 h-4 mr-2" />
                            {item.label}
                        </Button>
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-auto">
                <div className="p-6">
                    {activeSection === 'agent-auto-update' && (
                        <Card className="border-border/40">
                            <CardHeader className="pb-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <CardTitle className="text-2xl font-semibold">Agent Auto Update</CardTitle>
                                </div>
                                <p className="text-muted-foreground">
                                    Configure automatic updates for the security agent on devices using this profile.
                                </p>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between">
                                    <div className="space-y-1">
                                        <Label className="text-base font-medium">Enable Auto Update</Label>
                                        <p className="text-sm text-muted-foreground">
                                            When enabled, agents will automatically update to the latest version.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={agentAutoUpdate}
                                        onCheckedChange={() => handleToggleSetting('agentAutoUpdate')}
                                        data-testid="switch-agent-auto-update"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* SHA-256 Whitelisting Section */}
                    {activeSection === 'whitelisting-hashes' && (
                        <div className="space-y-6">
                            {/* Header Card */}
                            <Card className="border-border/40">
                                <CardHeader className="pb-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                                    <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                </div>
                                                <CardTitle className="text-2xl font-semibold">SHA-256 Whitelisting Hashes</CardTitle>
                                            </div>
                                            <p className="text-muted-foreground">
                                                Whitelisting controls the applications users can download and install.
                                                <br />
                                                Only installers in whitelist hashes will be allowed to be installed by the user.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                checked={sha256WhitelistingEnabled}
                                                onCheckedChange={() => handleToggleSetting('sha256WhitelistingEnabled')}
                                                data-testid="switch-sha256-whitelisting"
                                            />
                                            <Button
                                                onClick={() => setShowAddHashDialog(true)}
                                                disabled={!sha256WhitelistingEnabled}
                                                size="sm"
                                                data-testid="button-add-hash"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Add Hash
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                            </Card>

                            {/* Hash List */}
                            {sha256WhitelistingEnabled && (
                                <Card className="border-border/40">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="relative w-64">
                                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                                    <Input
                                                        placeholder="Search Hash"
                                                        value={hashSearchTerm}
                                                        onChange={(e) => {
                                                            setHashSearchTerm(e.target.value);
                                                            setCurrentPage(1);
                                                        }}
                                                        className="pl-10"
                                                        data-testid="input-search-hash"
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={handleDeleteSelected}
                                                disabled={selectedHashes.length === 0}
                                                data-testid="button-delete-selected"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Delete
                                            </Button>
                                        </div>

                                        {filteredHashes.length === 0 ? (
                                            <div className="text-center py-8">
                                                <p className="text-muted-foreground">No hashes available</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Table Header */}
                                                <div className="grid grid-cols-12 gap-4 p-3 bg-muted/50 rounded-lg mb-2 text-sm font-medium">
                                                    <div className="col-span-1 flex items-center">
                                                        <Checkbox
                                                            checked={selectedHashes.length === paginatedHashes.length && paginatedHashes.length > 0}
                                                            onCheckedChange={(checked) => {
                                                                if (checked) {
                                                                    setSelectedHashes(paginatedHashes.map(h => h.hashValue));
                                                                } else {
                                                                    setSelectedHashes([]);
                                                                }
                                                            }}
                                                            data-testid="checkbox-select-all"
                                                        />
                                                    </div>
                                                    <div className="col-span-7">Hash value ({whitelistHashes?.whitelistHashes.length})</div>
                                                    <div className="col-span-2">Hash type</div>
                                                    <div className="col-span-2">Created At</div>
                                                </div>

                                                {/* Table Rows */}
                                                <div className="space-y-2">
                                                    {paginatedHashes.map((hash) => (
                                                        <div key={hash.hashId} className="grid grid-cols-12 gap-4 p-3 border border-border/40 rounded-lg hover-elevate">
                                                            <div className="col-span-1 flex items-center">
                                                                <Checkbox
                                                                    checked={selectedHashes.includes(String(hash.hashId))}
                                                                    onCheckedChange={(checked) => handleSelectHash(hash.hashValue, checked as boolean)}
                                                                    data-testid={`checkbox-hash-${hash.hashId}`}
                                                                />
                                                            </div>
                                                            <div className="col-span-7 flex flex-col">
                                                                <span className="font-mono text-sm break-all" data-testid={`text-hash-value-${hash.hashId}`}>
                                                                    {hash.hashValue}
                                                                </span>
                                                                {hash.whitelistLabel && (
                                                                    <span className="text-xs text-muted-foreground">Label: {hash.whitelistLabel}</span>
                                                                )}
                                                            </div>
                                                            <div className="col-span-2 flex items-center">
                                                                <Badge variant="secondary" className="text-xs">
                                                                    {hash.hashType}
                                                                </Badge>
                                                            </div>
                                                            <div className="col-span-2 flex items-center text-sm text-muted-foreground">
                                                                {formatDate(hash.createdAt)}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <div className="flex items-center justify-between mt-6">
                                                        <div className="text-sm text-muted-foreground">
                                                            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredHashes.length)} of {filteredHashes.length} hashes
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                disabled={currentPage === 1}
                                                                data-testid="button-prev-page"
                                                            >
                                                                Previous
                                                            </Button>
                                                            <div className="flex items-center gap-1">
                                                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                                                    <Button
                                                                        key={page}
                                                                        variant={currentPage === page ? "default" : "outline"}
                                                                        size="sm"
                                                                        onClick={() => setCurrentPage(page)}
                                                                        className="w-8 h-8 p-0"
                                                                        data-testid={`button-page-${page}`}
                                                                    >
                                                                        {page}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                disabled={currentPage === totalPages}
                                                                data-testid="button-next-page"
                                                            >
                                                                Next
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Hash Dialog */}
            <Dialog open={showAddHashDialog} onOpenChange={setShowAddHashDialog}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Update Hashes</DialogTitle>
                    </DialogHeader>

                    <Tabs value={hashDialogTab} onValueChange={setHashDialogTab} className="w-full">
                        <div className="flex justify-center mb-4">
                            <TabsList className="grid grid-cols-2 w-80 bg-muted/50 p-1 h-11">
                                <TabsTrigger
                                    value="manual"
                                    className="data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-medium"
                                >
                                    Update Manually
                                </TabsTrigger>
                                <TabsTrigger
                                    value="file"
                                    className="data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm font-medium"
                                >
                                    File Upload
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        <TabsContent value="manual" className="space-y-4">
                            <div className="space-y-2">
                                <Label>Hash Value (Optional)</Label>
                                <p className="text-sm text-muted-foreground">Enter SHA-256 hash value:</p>
                                <Textarea
                                    placeholder="Enter SHA-256 hash values, one per line..."
                                    value={newHashInput}
                                    onChange={(e) => setNewHashInput(e.target.value)}
                                    className="min-h-24"
                                    data-testid="textarea-hash-input"
                                />
                            </div>
                            <Button
                                onClick={handleAddHash}
                                disabled={!newHashInput.trim()}
                                className="w-full"
                                data-testid="button-add-hash-manual"
                            >
                                Add Hash
                            </Button>
                        </TabsContent>

                        <TabsContent value="file" className="space-y-4">
                            <div
                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${isDragOver
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                                    : 'border-border hover:border-blue-300'
                                    }`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <Upload className={`w-8 h-8 mx-auto mb-4 transition-colors ${isDragOver ? 'text-blue-500' : 'text-muted-foreground'
                                    }`} />
                                <p className="text-sm text-muted-foreground mb-4">
                                    {isDragOver
                                        ? 'Drop your file here...'
                                        : 'Drag and drop a text file with SHA-256 hashes here'
                                    }
                                    <br />
                                    Or
                                </p>
                                <div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".txt,.csv"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                        data-testid="input-file-upload"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        data-testid="button-select-file"
                                    >
                                        Select File
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Supported formats: .txt, .csv
                                </p>
                            </div>
                        </TabsContent>
                    </Tabs>

                    {/* Pending Hashes */}
                    {pendingHashes.length > 0 && (
                        <div className="space-y-2">
                            <Label>Pending Hashes ({pendingHashes.length})</Label>
                            <div className="max-h-32 overflow-y-auto space-y-1">
                                {pendingHashes.map((hash, index) => (
                                    <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded text-xs">
                                        <span className="font-mono flex-1 truncate">{hash}</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemovePendingHash(hash)}
                                            className="h-6 w-6 p-0"
                                            data-testid={`button-remove-hash-${index}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAddHashDialog(false);
                                setPendingHashes([]);
                                setNewHashInput('');
                            }}
                            data-testid="button-close-dialog"
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleSubmitHashes}
                            disabled={pendingHashes.length === 0}
                            data-testid="button-submit-hashes"
                        >
                            Submit Hashes
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-destructive" />
                            Confirm Deletion
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-muted-foreground">
                            Are you sure you want to delete {selectedHashes.length} selected hash{selectedHashes.length !== 1 ? 'es' : ''}?
                            This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowDeleteDialog(false)}
                                data-testid="button-cancel-delete"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={confirmDelete}
                                data-testid="button-confirm-delete"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete {selectedHashes.length} Hash{selectedHashes.length !== 1 ? 'es' : ''}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}